/**
 * Decimal precision helpers. Must stay behaviourally identical to
 * `eInvoice/src/utils/decimal.ts`, since the UI and the backend compute the same
 * line totals independently.
 */

export const MIN_DECIMAL_PRECISION = 2;
export const MAX_DECIMAL_PRECISION = 6;

export const DEFAULT_DECIMAL_PRECISION = 2;

/** Decimal places for displaying a percentage. */
export const RATE_DISPLAY_PRECISION = 2;

/** `step` for percentage inputs. */
export const RATE_INPUT_STEP = "0.01";

/** Minimum decimal places for per-unit costs. */
export const UNIT_COST_MIN_PRECISION = 3;

/** Decimal places for document-level totals. Not configurable. */
export const TOTAL_PRECISION = 2;

export const ALLOWED_DECIMAL_PRECISIONS = [2, 3, 4, 5, 6] as const;

export type DecimalPrecision = (typeof ALLOWED_DECIMAL_PRECISIONS)[number];

export const DECIMAL_PRECISION_OPTIONS: {
	value: DecimalPrecision;
	label: string;
}[] = ALLOWED_DECIMAL_PRECISIONS.map((p) => ({
	value: p,
	label:
		p === DEFAULT_DECIMAL_PRECISION
			? `${p} Decimal Places (Default)`
			: `${p} Decimal Places`,
}));

/** Coerces an untrusted value into a valid precision, falling back to the default. */
export const resolvePrecision = (value: unknown): DecimalPrecision => {
	const n = typeof value === "number" ? value : Number(value);
	if (!Number.isInteger(n)) return DEFAULT_DECIMAL_PRECISION;
	if (n < MIN_DECIMAL_PRECISION || n > MAX_DECIMAL_PRECISION) {
		return DEFAULT_DECIMAL_PRECISION;
	}
	return n as DecimalPrecision;
};

/** Number of decimal places present in a value. Handles exponential notation. */
export const decimalPlacesOf = (value: number): number => {
	if (!Number.isFinite(value)) return 0;
	if (Number.isInteger(value)) return 0;

	const str = Math.abs(value).toString();
	const expMatch = str.match(/^(\d+)(?:\.(\d+))?e([+-]\d+)$/i);
	if (expMatch) {
		const fractionDigits = expMatch[2]?.length ?? 0;
		const exponent = Number(expMatch[3]);
		return Math.max(0, fractionDigits - exponent);
	}

	return str.split(".")[1]?.length ?? 0;
};

/**
 * Rounds to `precision` decimal places, half away from zero.
 *
 * Shifts via decimal string rather than `value * 10 ** precision`, which would
 * carry representation error into the rounding decision.
 */
export const roundTo = (
	value: number,
	precision: DecimalPrecision | number,
): number => {
	if (!Number.isFinite(value)) return 0;

	const scale = Math.max(0, Math.trunc(precision));
	const sign = value < 0 ? -1 : 1;
	const abs = Math.abs(value);
	// Every double at or above 2^53 is an integer, so there is nothing to round.
	// Shifting one would only overflow towards Infinity.
	if (abs >= 2 ** 53) return value;

	const absStr = abs.toString();

	if (absStr.includes("e") || absStr.includes("E")) {
		const factor = 10 ** scale;
		const scaled = abs * factor;
		return (sign * Math.round(scaled + scaled * Number.EPSILON)) / factor;
	}

	const shifted = Number(`${absStr}e${scale}`);

	// The nudge corrects ~1 ULP of representation error. Past 2^49 one ULP exceeds
	// half a unit and would flip the result instead.
	const NUDGE_SAFE_LIMIT = 2 ** 49;
	const rounded =
		shifted < NUDGE_SAFE_LIMIT
			? Math.round(shifted + shifted * Number.EPSILON)
			: Math.round(shifted);

	// String(n) goes exponential at 1e21, and Number("1e+21e-6") is NaN.
	const roundedStr = rounded.toString();
	const result =
		roundedStr.includes("e") || roundedStr.includes("E")
			? rounded / 10 ** scale
			: Number(`${roundedStr}e-${scale}`);

	return result === 0 ? 0 : sign * result;
};

// Float-noise budget for exceedsPrecision, as a proportion of the value and as
// fractions of one unit in the last decimal place.
const PRECISION_NOISE_RATIO = 1e-12;
const PRECISION_NOISE_FLOOR_FRACTION = 1e-6;
const PRECISION_NOISE_CEILING_FRACTION = 0.01;

/**
 * True when a value carries more decimal places than `precision` allows.
 *
 * Compares against the round-trip rather than the decimal-string length, so
 * IEEE-754 artifacts like `0.1 + 0.2` (`0.30000000000000004`) still pass at 2dp.
 */
export const exceedsPrecision = (
	value: number,
	precision: DecimalPrecision | number,
): boolean => {
	if (!Number.isFinite(value)) return true;

	const scale = Math.max(0, Math.trunc(precision));
	const delta = Math.abs(value - roundTo(value, scale));

	const gridUnit = 10 ** -scale;
	const tolerance = Math.min(
		Math.max(
			Math.abs(value) * PRECISION_NOISE_RATIO,
			gridUnit * PRECISION_NOISE_FLOOR_FRACTION,
		),
		gridUnit * PRECISION_NOISE_CEILING_FRACTION,
	);

	return delta > tolerance;
};

/** Coerces any input onto the precision grid. Non-numeric input becomes 0. */
export const normalize = (
	value: unknown,
	precision: DecimalPrecision | number,
): number => {
	const n = typeof value === "number" ? value : Number(value);
	if (!Number.isFinite(n)) return 0;
	return roundTo(n, precision);
};

/** Fixed-point string, e.g. `format(1.005, 2)` -> `"1.01"`. */
export const format = (
	value: number,
	precision: DecimalPrecision | number,
): string => {
	const scale = Math.max(0, Math.trunc(precision));
	return roundTo(value, scale).toFixed(scale);
};

/** Thousands-separated, e.g. `"1,234,567.50"`. */
export const formatWithSeparators = (
	value: number,
	precision: DecimalPrecision | number,
): string => {
	const [integerPart, fractionPart] = format(value, precision).split(".");
	const grouped = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
	return fractionPart ? `${grouped}.${fractionPart}` : grouped;
};

/** Currency string, e.g. `"RM1,234.50"`. */
export const formatCurrency = (
	value: number,
	precision: DecimalPrecision | number,
	currencySymbol = "RM",
): string => `${currencySymbol}${formatWithSeparators(value, precision)}`;

/** `step` attribute for a numeric input, e.g. `4 -> "0.0001"`. */
export const stepForPrecision = (
	precision: DecimalPrecision | number,
): string => {
	const scale = Math.max(0, Math.trunc(precision));
	return scale === 0 ? "1" : `0.${"0".repeat(scale - 1)}1`;
};

/** Rounds a document-level total. */
export const toTotalAmount = (value: number): number =>
	roundTo(value, TOTAL_PRECISION);

/** Rounds a computed line value, keeping storage precision. */
export const toLineAmount = (value: number): number =>
	roundTo(value, MAX_DECIMAL_PRECISION);

/** Sums a list, rounding once at the end so float error cannot compound. */
export const sum = (
	values: readonly number[],
	precision: DecimalPrecision | number,
): number =>
	roundTo(
		values.reduce<number>((acc, v) => acc + (Number.isFinite(v) ? v : 0), 0),
		precision,
	);
